/**
 * In-memory, session-scoped store of the newest-applied-change timestamp (ISO
 * 8601) per entity-type. Must never be persisted — that would falsely signal
 * "caught up" when the data it was derived from is gone.
 */

/** Entity-types this store tracks a cursor for; only `'case'` exists so far. */
export type CursorEntityType = 'case';

const cursors = new Map<CursorEntityType, string>();

/** Current cursor for `type`, or `''` if none has been set yet. */
export const getCursor = (type: CursorEntityType): string =>
  cursors.get(type) ?? '';

/**
 * Advance the cursor for `type` to `ts` if `ts` is later (lexicographically
 * greater — ISO 8601 strings sort chronologically). An empty `ts` is ignored.
 */
export const advance = (type: CursorEntityType, ts: string): void => {
  if (ts === '') {
    return;
  }
  const current = getCursor(type);
  if (ts > current) {
    cursors.set(type, ts);
  }
};

/**
 * Seed the cursor to the min of non-empty `candidates` and the existing cursor,
 * so independent per-endpoint seeds converge; `seed` only moves it down, `advance` up.
 */
export const seed = (type: CursorEntityType, candidates: string[]): void => {
  const current = getCursor(type);
  const pool = [current, ...candidates].filter(value => value !== '');
  if (pool.length === 0) {
    return;
  }
  cursors.set(
    type,
    pool.reduce((min, candidate) => (candidate < min ? candidate : min)),
  );
};

/** Empty every cursor. */
export const clear = (): void => {
  cursors.clear();
};
